// src/hooks/useUserIAM.ts

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import {
  setUserIAM,
  resetUserIAM,
  setUpdatedUserIAM,
} from 'src/redux/IAM/AdminUser/UserIAMSlice'
import { getUsers } from 'src/services/IAM/AdminUser/AdminUser.service'
import { useAlert } from 'src/hooks/useAlert'
import { IUser } from 'src/services/IAM/AdminUser/AdminUser.interface'
import { usePagination } from '../Components/Pagination/usePagination'
import useLoading from 'src/hooks/useLoading'

/**
 * Custom hook for managing user IAM (Identity Access Management) functionalities.
 * It provides functions to load users, set the current user, reset the user state,
 * and manage loading and alert states.
 *
 * @returns An object containing the current user, a list of users, updated user information,
 *          and various methods for managing user IAM.
 */
export const useUserIAM = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { userIAM, usersIAM, updatedUserIAM } = useSelector(
    (state: RootState) => state.userIAM,
  )
  const { handleTotalItems } = usePagination()
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()

  /**
   * Loads users from the server with optional pagination parameters.
   * Displays alerts based on the success or failure of the request.
   *
   * @param pagination - An object containing pagination parameters.
   * @param pagination.currentPage - The current page number.
   * @param pagination.pageSize - The number of items per page.
   * @param pagination.type - The type of users to load (e.g., 'all').
   */
  const loadUsers = (pagination: {
    currentPage?: number
    pageSize?: number
    type?: 'all'
  }) => {
    startLoading()
    dispatch(getUsers())
      .then((result) => {
        stopLoading()
        if (getUsers.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al cargar los usuarios',
          )
        } else if (getUsers.fulfilled.match(result)) {
          showAlert('success', 'Usuarios cargados correctamente')
          if (pagination) {
            console.log(result.payload.details, 'result.payload.detailsss')
            console.log('usuarios cargados correctamente')

            handleTotalItems(result.payload.details)
          }
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Sets the current user IAM state.
   *
   * @param user - The user object to set.
   */
  const setUserIAMAdmin = (user: IUser) => {
    dispatch(setUserIAM(user))
  }

  /**
   * Sets the updated user IAM state.
   *
   * @param user - The user object with updated information.
   */
  const setUserUpdatedIAMAdmin = (user: IUser) => {
    dispatch(setUpdatedUserIAM(user))
  }

  /**
   * Clears the user IAM state, resetting it to its initial state.
   */
  const clearUserIAM = () => {
    dispatch(resetUserIAM())
  }

  return {
    userIAM,
    usersIAM,
    updatedUserIAM,
    setUserUpdatedIAMAdmin,
    setUserIAMAdmin,
    clearUserIAM,
    loadUsers,
  }
}
